import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/aircraft-status';
import { DigitaiAircraftStatus, DigitaiCreateAircraftStatus, DigitaiUpdateAircraftStatusById, DigitaiUpdateAircraftStatuses } from '@apps/digitai/digitai.types';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AircraftStatusService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiAircraftStatus> | null> = new BehaviorSubject(null);
    aircraftStatusSubject$: BehaviorSubject<DigitaiAircraftStatus | null> = new BehaviorSubject(null);
    aircraftStatusesSubject$: BehaviorSubject<DigitaiAircraftStatus[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiAircraftStatus> | null>; } = {};
    aircraftStatusScoped: { [key: string]: BehaviorSubject<DigitaiAircraftStatus | null>; } = {};
    aircraftStatusesScoped: { [key: string]: BehaviorSubject<DigitaiAircraftStatus[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiAircraftStatus>>
    {
        return this.paginationSubject$.asObservable();
    }

    get aircraftStatus$(): Observable<DigitaiAircraftStatus>
    {
        return this.aircraftStatusSubject$.asObservable();
    }

    get aircraftStatuses$(): Observable<DigitaiAircraftStatus[]>
    {
        return this.aircraftStatusesSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiAircraftStatus>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiAircraftStatus>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();
        return null;
    }

    setScopeAircraftStatus(scope: string, object: DigitaiAircraftStatus): void
    {
        if (this.aircraftStatusScoped[scope])
        {
            this.aircraftStatusScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.aircraftStatusScoped[scope] = new BehaviorSubject(object);
    }

    getScopeAircraftStatus(scope: string): Observable<DigitaiAircraftStatus>
    {
        if (this.aircraftStatusScoped[scope]) return this.aircraftStatusScoped[scope].asObservable();
        return null;
    }

    setScopeAircraftStatuses(scope: string, objects: DigitaiAircraftStatus[]): void
    {
        if (this.aircraftStatusesScoped[scope])
        {
            this.aircraftStatusesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.aircraftStatusesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeAircraftStatuses(scope: string): Observable<DigitaiAircraftStatus[]>
    {
        if (this.aircraftStatusesScoped[scope]) return this.aircraftStatusesScoped[scope].asObservable();
        return null;
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiAircraftStatus>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiAircraftStatus>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = '',
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftStatus;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftStatus;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftStatus(scope, data.object) : this.aircraftStatusSubject$.next(data.object)),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftStatus;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftStatus;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftStatus(scope, data.object) : this.aircraftStatusSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiAircraftStatus[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiAircraftStatus[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftStatuses(scope, data.objects) : this.aircraftStatusesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateAircraftStatus;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateAircraftStatusById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateAircraftStatuses;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = '',
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
